import type { Position } from 'geojson'
import type { GeoJSONType } from '@/storage/site'

type Point = { latitude: number; longitude: number }
type Polygon = Point[]

function getDistanceBetweenPoints(a: Point, b: Point): number {
  const earthRadius = 6378.137 // kilometers
  const dLat = (b.latitude * Math.PI) / 180 - (a.latitude * Math.PI) / 180
  const dLon = (b.longitude * Math.PI) / 180 - (a.longitude * Math.PI) / 180
  const sideA =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((a.latitude * Math.PI) / 180) *
      Math.cos((b.latitude * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)
  const sideC = 2 * Math.atan2(Math.sqrt(sideA), Math.sqrt(1 - sideA))
  const distance = earthRadius * sideC
  return distance * 1000 // meters
}

function pointFromCoordinates([longitude, latitude]: Position): Point {
  return {
    latitude,
    longitude,
  }
}

function toPolygon(coordinates: Position[][]): Polygon | null {
  const ring = coordinates[0]

  if (!ring || ring.length < 3) {
    return null
  }

  return ring.map(pointFromCoordinates)
}

function isPointInsidePolygon(point: Point, polygon: Polygon): boolean {
  let inside = false

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const current = polygon[i]
    const previous = polygon[j]
    const intersects =
      current.latitude > point.latitude !==
        previous.latitude > point.latitude &&
      point.longitude <
        ((previous.longitude - current.longitude) *
          (point.latitude - current.latitude)) /
          (previous.latitude - current.latitude) +
          current.longitude

    if (intersects) {
      inside = !inside
    }
  }

  return inside
}

function toLocalCoordinates(point: Point, referenceLatitude: number) {
  const metersPerDegreeLatitude = 111132
  const metersPerDegreeLongitude =
    111320 * Math.cos((referenceLatitude * Math.PI) / 180)

  return {
    x: point.longitude * metersPerDegreeLongitude,
    y: point.latitude * metersPerDegreeLatitude,
  }
}

function getDistanceBetweenPointAndSegment(
  point: Point,
  segmentStart: Point,
  segmentEnd: Point,
): number {
  const referenceLatitude =
    (point.latitude + segmentStart.latitude + segmentEnd.latitude) / 3
  const p = toLocalCoordinates(point, referenceLatitude)
  const a = toLocalCoordinates(segmentStart, referenceLatitude)
  const b = toLocalCoordinates(segmentEnd, referenceLatitude)

  const abX = b.x - a.x
  const abY = b.y - a.y
  const apX = p.x - a.x
  const apY = p.y - a.y
  const projection =
    (apX * abX + apY * abY) / (abX * abX + abY * abY || Number.EPSILON)
  const t = Math.max(0, Math.min(1, projection))
  const closestX = a.x + t * abX
  const closestY = a.y + t * abY

  return Math.hypot(p.x - closestX, p.y - closestY)
}

function getDistanceBetweenPointAndPolygon(
  point: Point,
  polygon: Polygon,
): number {
  if (isPointInsidePolygon(point, polygon)) {
    return 0
  }

  let minimumDistance = Number.POSITIVE_INFINITY

  for (let i = 0; i < polygon.length; i++) {
    const segmentStart = polygon[i]
    const segmentEnd = polygon[(i + 1) % polygon.length]
    const distance = getDistanceBetweenPointAndSegment(
      point,
      segmentStart,
      segmentEnd,
    )

    minimumDistance = Math.min(minimumDistance, distance)
  }

  return minimumDistance
}

function getDistanceBetweenLocations(
  user: Point,
  siteLocation: GeoJSONType,
): number {
  if (siteLocation.type === 'Point') {
    return getDistanceBetweenPoints(
      user,
      pointFromCoordinates(siteLocation.coordinates),
    )
  }

  const polygon = toPolygon(siteLocation.coordinates)

  if (!polygon) {
    return Number.POSITIVE_INFINITY
  }

  return getDistanceBetweenPointAndPolygon(user, polygon)
}

export default function overlap(
  user: Point,
  siteLocation: GeoJSONType,
  overlapRadius: number,
): boolean {
  const distance = getDistanceBetweenLocations(user, siteLocation)

  return distance < overlapRadius
}
